import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { generatedMagzineRefetchInterval } from '../../constants';
import { magazinePublishStrings } from './publishStrings';

/**
 * "Publish magazine" regenerates the member's magazine (a non-preview render)
 * and then asks Digital Services to send it to print. The regeneration is
 * asynchronous: DS stores the generated magazine as `PENDING` and a lambda
 * renders the PDF and marks it `DONE`. The editor used to ask for the print
 * right after starting the regeneration, with the id of the generated magazine
 * loaded before it — `undefined` on the first publish of an issue, so DS
 * answered 500 to `POST /generated-magazines/undefined/print` and nothing was
 * sent.
 *
 * This module waits until the render the regeneration started is printable,
 * and only then asks for the print, with that render's id. It never depends on
 * the landing-page step (`pageStatus`): DS's print guard does not either.
 *
 * Known limit: DS regenerates IN PLACE (the active record keeps its id and is
 * set back to `PENDING`), and the lambda's `DONE` does not say which render it
 * finished. If a preview render (started on mount or after step 4) is still
 * running when the member publishes, its `DONE` lands on the record the publish
 * just set to non-preview, and the print is filed before the non-preview
 * render ends. The PDFs come from the same magazine data, so what is printed
 * is the same; telling the two apart needs a render token from DS. Waiting for
 * `flippingBookUrl` (only the non-preview render writes it) would block the
 * print whenever FlippingBook fails, and printing does not need it.
 */

/** Same cadence the DMP already uses to poll a generating magazine. */
export const GENERATION_POLL_INTERVAL_MS = generatedMagzineRefetchInterval;

/** How long the editor waits before telling the member to try again. */
export const GENERATION_TIMEOUT_MS = 3 * 60 * 1000;

/**
 * Timeouts on one render before the next publish starts a new one. The lambda
 * never writes `ERROR`: a render that dies stays `PENDING` for good.
 */
export const TIMEOUTS_BEFORE_RESTART = 2;

/** DS `GenerationStatus`. */
export const generationStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  DONE: 'DONE',
  ERROR: 'ERROR',
  SENT_FOR_PRINTING: 'SENT_FOR_PRINTING',
};

/**
 * Where a generated magazine stands, relative to the render this flow started:
 * `ready`, `pending`, `failed`, `superseded` or `alreadySent`.
 *
 * `ready` is what DS's print guards accept (`sendToPrint` and, for operators,
 * `assertOperatorPrintable`): active, not a preview, `DONE`, with a magazine or
 * covers PDF. The lambda writes `DONE` together with the PDF urls, so a `DONE`
 * record without one, or a `DONE` preview, will not become printable by
 * waiting: that is a failed render, not a pending one.
 *
 * @param {object|undefined|null} magazine the generated magazine read back.
 * @param {string} [expectedId] the id the regeneration returned. The read
 * returns the member's ACTIVE render, so a different id means a newer render
 * replaced it.
 */
export const classifyGeneratedMagazine = (magazine, expectedId) => {
  if (!magazine) {
    return 'pending';
  }
  if (magazine.active === false || (expectedId && magazine.id && magazine.id !== expectedId)) {
    return 'superseded';
  }
  if (magazine.status === generationStatus.ERROR) {
    return 'failed';
  }
  if (magazine.status === generationStatus.SENT_FOR_PRINTING) {
    return 'alreadySent';
  }
  if (magazine.status !== generationStatus.DONE) {
    return 'pending';
  }
  if (magazine.isPreview || (!magazine.url && !magazine.coversOnlyUrl) || !magazine.id) {
    return 'failed';
  }
  return 'ready';
};

const abortableSleep = (ms, signal) =>
  new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    let timer;
    function done() {
      clearTimeout(timer);
      signal?.removeEventListener('abort', done);
      resolve();
    }
    timer = setTimeout(done, ms);
    signal?.addEventListener('abort', done);
  });

/**
 * `promise`, or `undefined` once `ms` pass or `signal` aborts, whichever comes
 * first. The api instance sets no request timeout, so a read that never
 * answers would otherwise hold the poll (and the loader) past its deadline.
 */
const settleWithin = (promise, ms, signal) =>
  new Promise((resolve, reject) => {
    let timer;
    function finish(settle) {
      clearTimeout(timer);
      // eslint-disable-next-line no-use-before-define
      signal?.removeEventListener('abort', giveUp);
      settle();
    }
    function giveUp() {
      finish(() => resolve(undefined));
    }
    if (signal?.aborted) {
      resolve(undefined);
      return;
    }
    timer = setTimeout(giveUp, ms);
    signal?.addEventListener('abort', giveUp);
    promise.then(
      (value) => finish(() => resolve(value)),
      (error) => finish(() => reject(error)),
    );
  });

/**
 * Polls the member's generated magazine until it is printable, settles in a
 * state waiting cannot change, the timeout passes, or `signal` aborts. A failed
 * or unanswered read (DS answers 404 until the record exists) is treated as
 * transient: the next poll tries again.
 *
 * @returns {Promise<{ kind: string, magazine?: object }>} `kind` is `ready`
 * (with `magazine`), `failed`, `superseded`, `alreadySent`, `timeout` or
 * `aborted`.
 */
export const waitForPrintableMagazine = async ({
  fetchGenerated,
  expectedId,
  signal,
  intervalMs = GENERATION_POLL_INTERVAL_MS,
  timeoutMs = GENERATION_TIMEOUT_MS,
}) => {
  const deadline = Date.now() + timeoutMs;

  for (;;) {
    if (signal?.aborted) {
      return { kind: 'aborted' };
    }

    let magazine;
    try {
      // eslint-disable-next-line no-await-in-loop
      magazine = await settleWithin(fetchGenerated(), Math.max(0, deadline - Date.now()), signal);
    } catch {
      magazine = undefined;
    }

    if (signal?.aborted) {
      return { kind: 'aborted' };
    }

    const state = classifyGeneratedMagazine(magazine, expectedId);
    if (state === 'ready') {
      return { kind: 'ready', magazine };
    }
    if (state !== 'pending') {
      return { kind: state };
    }
    if (Date.now() + intervalMs > deadline) {
      return { kind: 'timeout' };
    }

    // eslint-disable-next-line no-await-in-loop
    await abortableSleep(intervalMs, signal);
  }
};

/**
 * The id of the render a regeneration created. The member api resolves with
 * the response body and the admin api with the axios response; accept either.
 */
export const generatedMagazineIdFrom = (response) => {
  const id = response?.data?.id ?? response?.id;
  return typeof id === 'string' && id ? id : undefined;
};

/** The generated magazine read may answer with one record or a list. */
export const firstGeneratedMagazine = (data) => (Array.isArray(data) ? data[0] : data);

const outcomeMessage = {
  failed: magazinePublishStrings.generationFailed,
  superseded: magazinePublishStrings.superseded,
  alreadySent: magazinePublishStrings.alreadySent,
};

/**
 * Regenerate, wait for the render to be printable, then send it to print.
 *
 * `deps`:
 * - `startGeneration()` starts a non-preview regeneration and resolves with
 *   the new render's id (or `undefined` when the response carries none).
 * - `fetchGenerated()` reads the member's active generated magazine.
 * - `sendToPrint(magazine, signal)` files the print job for the printable
 *   `magazine` (its `id` is always set). `signal` aborts when the editor
 *   unmounts: check it before doing anything after the request, such as
 *   navigating. A rejection is the caller's to report.
 * - `notify(message)` shows a message this flow produces itself.
 *
 * Behaviour:
 * - One flow at a time: a repeat click while one runs (including while
 *   `prepare` runs) does nothing, so the magazine is never regenerated or sent
 *   twice.
 * - After a timeout the started render is remembered, so the next `publish`
 *   resumes waiting on it instead of starting another regeneration, up to
 *   `TIMEOUTS_BEFORE_RESTART` timeouts; the publish after that starts a new
 *   one. Call `forgetPendingGeneration` when the magazine is edited, so the
 *   next `publish` renders the edit.
 * - Unmounting stops the polling and nothing is sent afterwards.
 * - `phase` (`idle`, `generating`, `sending`) always returns to `idle` when a
 *   flow ends, however it ends.
 */
export const usePublishWhenGenerated = (deps) => {
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const [phase, setPhase] = useState('idle');
  const inFlightRef = useRef(false);
  const pendingGenerationRef = useRef(null);
  const controllerRef = useRef(null);
  const unmountedRef = useRef(false);

  // A layout effect so the abort happens synchronously when the flow itself
  // navigates away (the editor unmounts inside `sendToPrint`).
  useLayoutEffect(
    () => () => {
      unmountedRef.current = true;
      controllerRef.current?.abort();
    },
    [],
  );

  const forgetPendingGeneration = useCallback(() => {
    pendingGenerationRef.current = null;
  }, []);

  /**
   * @param {() => Promise<boolean>} [prepare] runs first, inside the
   * one-flow-at-a-time guard; resolving `false` stops the flow (e.g. a form
   * validation error) without showing the loader.
   */
  const publish = useCallback(async (prepare) => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;

    const controller = new AbortController();
    controllerRef.current = controller;
    const { signal } = controller;

    try {
      if (prepare && !(await prepare())) {
        return;
      }
      if (unmountedRef.current) {
        return;
      }
      setPhase('generating');

      if (pendingGenerationRef.current?.timeouts >= TIMEOUTS_BEFORE_RESTART) {
        pendingGenerationRef.current = null;
      }
      if (!pendingGenerationRef.current) {
        try {
          const id = await depsRef.current.startGeneration();
          pendingGenerationRef.current = { id, timeouts: 0 };
        } catch {
          if (!signal.aborted) {
            depsRef.current.notify(magazinePublishStrings.couldNotStart);
          }
          return;
        }
      }

      const generation = pendingGenerationRef.current;
      const { intervalMs, timeoutMs } = depsRef.current;
      const outcome = await waitForPrintableMagazine({
        fetchGenerated: () => depsRef.current.fetchGenerated(),
        expectedId: generation.id,
        signal,
        intervalMs,
        timeoutMs,
      });

      if (outcome.kind === 'aborted' || signal.aborted) {
        return;
      }
      if (outcome.kind === 'timeout') {
        generation.timeouts += 1;
        depsRef.current.notify(
          generation.timeouts >= TIMEOUTS_BEFORE_RESTART
            ? magazinePublishStrings.startOver
            : magazinePublishStrings.stillGenerating,
        );
        return;
      }

      pendingGenerationRef.current = null;
      if (outcome.kind !== 'ready') {
        depsRef.current.notify(outcomeMessage[outcome.kind]);
        return;
      }

      setPhase('sending');
      try {
        await depsRef.current.sendToPrint(outcome.magazine, signal);
      } catch {
        // Reported by the caller (see `sendToPrint`).
      }
    } finally {
      inFlightRef.current = false;
      if (!signal.aborted && !unmountedRef.current) {
        setPhase('idle');
      }
    }
  }, []);

  return { phase, publish, forgetPendingGeneration };
};
